import React, { useState } from 'react';
import {
  ShoppingBag, Wind, Bed, Activity, Stethoscope, Package,
  Plus, Minus, Check, ChevronRight, CheckCircle2, Info,
  FileText, Calendar, MapPin, User, Truck, X
} from 'lucide-react';
import { materiels, clientConnecte } from '../data/mockData';

const iconForCategorie = (categorie) => {
  switch (categorie) {
    case 'Oxygénothérapie': return Wind;
    case 'Apnée du sommeil': return Bed;
    case 'Ventilation assistée': return Activity;
    case 'Aspiration trachéale': return Stethoscope;
    default: return Package;
  }
};

const Catalogue = () => {
  const [panier, setPanier] = useState({});
  const [serviceLivraison, setServiceLivraison] = useState('');
  const [referencePatient, setReferencePatient] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [duree, setDuree] = useState(3);
  const [observations, setObservations] = useState('');
  const [step, setStep] = useState(1);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [bonCommandeNum, setBonCommandeNum] = useState('');

  const updateQte = (code, delta) => {
    setPanier(prev => {
      const next = { ...prev };
      const current = next[code] || 0;
      const newQte = Math.max(0, current + delta);
      if (newQte === 0) {
        delete next[code];
      } else {
        next[code] = newQte;
      }
      return next;
    });
  };

  const setQte = (code, qte) => {
    setPanier(prev => {
      const next = { ...prev };
      if (qte <= 0) {
        delete next[code];
      } else {
        next[code] = qte;
      }
      return next;
    });
  };

  const lignesPanier = Object.entries(panier).map(([code, qte]) => {
    const mat = materiels.find(m => m.code === code);
    return {
      ...mat,
      quantite: qte,
    };
  });

  const nbReferences = lignesPanier.length;

  const validerCommande = () => {
    const num = `BC-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    setBonCommandeNum(num);
    setOrderConfirmed(true);
    setStep(3);
  };

  const reinitialiser = () => {
    setPanier({});
    setServiceLivraison('');
    setReferencePatient('');
    setDateDebut('');
    setDuree(3);
    setObservations('');
    setStep(1);
    setOrderConfirmed(false);
    setBonCommandeNum('');
  };

  if (orderConfirmed) {
    return (
      <div className="page-container">
        <div className="confirmation-card">
          <div className="confirmation-icon">
            <CheckCircle2 size={56} />
          </div>
          <h1>Commande validée !</h1>
          <p className="confirmation-subtitle">
            Votre bon de commande a été généré automatiquement et transmis à nos équipes.
          </p>

          <div className="confirmation-details">
            <div className="confirmation-detail-row">
              <FileText size={18} />
              <div>
                <span className="confirmation-label">N° de bon de commande</span>
                <span className="confirmation-value mono-text">{bonCommandeNum}</span>
              </div>
            </div>
            <div className="confirmation-detail-row">
              <Calendar size={18} />
              <div>
                <span className="confirmation-label">Date de mise en location</span>
                <span className="confirmation-value">{dateDebut ? new Date(dateDebut).toLocaleDateString('fr-FR') : 'Sous 48h ouvrées'}</span>
              </div>
            </div>
            <div className="confirmation-detail-row">
              <Package size={18} />
              <div>
                <span className="confirmation-label">Matériels commandés</span>
                <span className="confirmation-value">{nbReferences} référence{nbReferences > 1 ? 's' : ''} — {Object.values(panier).reduce((a, b) => a + b, 0)} unité(s)</span>
              </div>
            </div>
            <div className="confirmation-detail-row">
              <Truck size={18} />
              <div>
                <span className="confirmation-label">Installation prévue par</span>
                <span className="confirmation-value">Technicien RESPIDOM (vous serez contacté sous 24h)</span>
              </div>
            </div>
          </div>

          <div className="confirmation-info">
            <Info size={16} />
            <p>Vous recevrez par email le détail du bon de commande ainsi que le rendez-vous d'installation.</p>
          </div>

          <div className="confirmation-actions">
            <button className="btn-primary" onClick={reinitialiser}>
              <ShoppingBag size={16} /> Nouvelle commande
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '/locations'}>
              Voir mes locations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Commander un matériel</h1>
          <p className="page-subtitle">Mise en location de matériel d'assistance respiratoire et médical — Génération automatique du bon de commande</p>
        </div>
      </div>

      {/* Étapes */}
      <div className="stepper">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-circle">{step > 1 ? <Check size={16} /> : '1'}</div>
          <span>Sélection du matériel</span>
        </div>
        <div className="step-line" />
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-circle">{step > 2 ? <Check size={16} /> : '2'}</div>
          <span>Informations & livraison</span>
        </div>
        <div className="step-line" />
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-circle">3</div>
          <span>Confirmation</span>
        </div>
      </div>

      {/* Étape 1 : Catalogue */}
      {step === 1 && (
        <div className="catalogue-layout">
          <div className="catalogue-grid">
            {materiels.map((mat) => {
              const Icon = iconForCategorie(mat.categorie);
              const qte = panier[mat.code] || 0;
              const isSelected = qte > 0;
              return (
                <div key={mat.code} className={`materiel-card ${isSelected ? 'selected' : ''}`}>
                  <div className="materiel-card-header">
                    <div className="materiel-icon">
                      <Icon size={28} />
                    </div>
                    <span className="materiel-categorie-tag">{mat.categorie}</span>
                  </div>

                  <div className="materiel-card-body">
                    <h3 className="materiel-nom">{mat.nom}</h3>
                    <p className="materiel-modele">{mat.modele} — {mat.fabricant}</p>
                    <p className="materiel-description">{mat.description}</p>

                    <div className="materiel-accessoires">
                      <span className="materiel-accessoires-label">Inclus :</span>
                      <div className="materiel-accessoires-list">
                        {mat.accessoires.map((acc, i) => (
                          <span key={i} className="materiel-acc-tag">{acc}</span>
                        ))}
                      </div>
                    </div>

                    <div className="materiel-meta">
                      <span><Info size={12} /> Stock : {mat.stock}</span>
                      <span><Calendar size={12} /> Durée min. : {mat.duréeMinimum} mois</span>
                    </div>
                  </div>

                  <div className="materiel-card-footer">
                    {qte === 0 ? (
                      <button className="btn-primary materiel-add-btn" onClick={() => setQte(mat.code, 1)}>
                        <Plus size={16} /> Ajouter
                      </button>
                    ) : (
                      <div className="qte-selector">
                        <button onClick={() => updateQte(mat.code, -1)}><Minus size={14} /></button>
                        <input
                          type="number"
                          min="0"
                          value={qte}
                          onChange={(e) => setQte(mat.code, parseInt(e.target.value) || 0)}
                        />
                        <button onClick={() => updateQte(mat.code, 1)}><Plus size={14} /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Panier latéral */}
          <aside className="panier">
            <div className="panier-header">
              <ShoppingBag size={18} />
              <h2>Mon panier</h2>
              <span className="panier-count">{nbReferences}</span>
            </div>

            {nbReferences === 0 ? (
              <div className="panier-empty">
                <Package size={32} strokeWidth={1.2} />
                <p>Aucun matériel sélectionné</p>
                <span>Ajoutez des matériels depuis le catalogue</span>
              </div>
            ) : (
              <>
                <div className="panier-list">
                  {lignesPanier.map((l) => (
                    <div key={l.code} className="panier-item">
                      <div className="panier-item-info">
                        <span className="panier-item-nom">{l.nom}</span>
                        <span className="panier-item-modele">{l.modele}</span>
                      </div>
                      <div className="panier-item-actions">
                        <div className="qte-selector qte-selector-sm">
                          <button onClick={() => updateQte(l.code, -1)}><Minus size={12} /></button>
                          <span>{l.quantite}</span>
                          <button onClick={() => updateQte(l.code, 1)}><Plus size={12} /></button>
                        </div>
                        <button className="btn-icon-sm panier-remove" onClick={() => setQte(l.code, 0)}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button className="btn-primary panier-validate" onClick={() => setStep(2)}>
                  Continuer <ChevronRight size={16} />
                </button>
              </>
            )}
          </aside>
        </div>
      )}

      {/* Étape 2 : Informations livraison */}
      {step === 2 && (
        <div className="commande-form-layout">
          <div className="commande-form">
            <div className="form-card">
              <h2 className="form-card-title"><MapPin size={18} /> Informations de livraison & installation</h2>

              <div className="form-grid">
                <div className="form-group">
                  <label>Service / Unité <span className="required">*</span></label>
                  <input
                    type="text"
                    placeholder="ex. Pneumologie - Chambre 215"
                    value={serviceLivraison}
                    onChange={(e) => setServiceLivraison(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Référence patient (anonymisée)</label>
                  <input
                    type="text"
                    placeholder="ex. PAT-2415"
                    value={referencePatient}
                    onChange={(e) => setReferencePatient(e.target.value)}
                  />
                  <span className="form-hint">Identifiant interne, non nominatif</span>
                </div>

                <div className="form-group">
                  <label>Date souhaitée de mise en location <span className="required">*</span></label>
                  <input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <span className="form-hint">Délai standard : 48h ouvrées</span>
                </div>

                <div className="form-group">
                  <label>Durée prévisionnelle de location <span className="required">*</span></label>
                  <select value={duree} onChange={(e) => setDuree(parseInt(e.target.value))}>
                    <option value={1}>1 mois</option>
                    <option value={3}>3 mois</option>
                    <option value={6}>6 mois</option>
                    <option value={12}>12 mois</option>
                    <option value={24}>24 mois (longue durée)</option>
                  </select>
                  <span className="form-hint">La location peut être arrêtée à tout moment depuis votre espace</span>
                </div>

                <div className="form-group form-group-full">
                  <label>Observations / Prescriptions particulières</label>
                  <textarea
                    rows={4}
                    placeholder="Réglages spécifiques, contraintes d'accès, etc."
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="form-card">
              <h2 className="form-card-title"><User size={18} /> Adresse de facturation & livraison</h2>
              <div className="address-display">
                <strong>{clientConnecte.raisonSociale}</strong>
                <p>{clientConnecte.adresse}</p>
                <p>{clientConnecte.codePostal} {clientConnecte.ville}</p>
                <p className="address-meta">N° FINESS : <span className="mono-text">{clientConnecte.numeroFiness}</span></p>
              </div>
            </div>
          </div>

          {/* Récapitulatif */}
          <aside className="panier panier-recap">
            <div className="panier-header">
              <FileText size={18} />
              <h2>Récapitulatif</h2>
            </div>

            <div className="panier-list">
              {lignesPanier.map((l) => (
                <div key={l.code} className="panier-item panier-item-readonly">
                  <div className="panier-item-info">
                    <span className="panier-item-nom">{l.nom}</span>
                    <span className="panier-item-modele">x{l.quantite}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="panier-summary">
              <div className="panier-summary-row">
                <span>Durée de location</span>
                <strong>{duree} mois</strong>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setStep(1)}>
                Retour
              </button>
              <button
                className="btn-primary"
                onClick={validerCommande}
                disabled={!serviceLivraison || !dateDebut}
              >
                <FileText size={16} /> Générer le bon de commande
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Catalogue;
